import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { resolveMedia } from "@/lib/api";
import { AVAIL_LABELS } from "@/lib/shopUtils";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);

// Carrello condiviso: trigger + drawer. `onInquiry` (opz.) mostra il CTA "Richiedi prezzo esclusivo".
export function CartSheet({ open, onOpenChange, onInquiry, dark = false, testId = "cart-sheet" }) {
  const cart = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const shippingCost = Number(settings.shop_shipping_cost) || 0;
  const freeOver = Number(settings.shop_free_shipping_over) || 0;
  const shippingNow = freeOver > 0 && cart.total >= freeOver ? 0 : shippingCost;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <button
          className={`inline-flex items-center gap-3 relative shrink-0 border px-6 py-3 min-h-[44px] text-xs tracking-[0.16em] uppercase transition-colors ${dark ? "border-bone/50 hover:bg-bone hover:text-ink" : "border-ink hover:bg-ink hover:text-bone"}`}
          data-testid={`${testId}-button`}
        >
          <ShoppingBag size={16} />
          Carrello
          {cart.count > 0 && (
            <span className="ml-1 bg-gold text-ink text-[10px] font-medium min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center" data-testid={`${testId}-count`}>
              {cart.count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md rounded-none flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-hair">
          <SheetTitle className="font-serif text-3xl text-left">Il tuo carrello</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cart.items.length === 0 && (
            <p className="text-taupe text-sm">Non hai ancora aggiunto prodotti.</p>
          )}
          {cart.items.map((i) => (
            <div key={i.key || i.product_id} className="flex gap-4 border-b border-hair pb-4" data-testid={`cart-row-${i.product_id}`}>
              <img src={resolveMedia(i.image)} alt={i.product_name} className="w-20 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg leading-tight">{i.product_name}</div>
                {i.variant && <div className="text-xs text-taupe mt-0.5" data-testid={`cart-variant-${i.product_id}`}>{i.variant}</div>}
                <div className="text-sm text-gold mt-1">{eur(i.price)}</div>
                {(i.availability === "da_ordinare" || i.lead_time) && (
                  <div className="text-[10px] tracking-[0.12em] uppercase text-amber-700 mt-1">
                    {AVAIL_LABELS[i.availability] || ""}{i.lead_time ? ` · ${i.lead_time}` : ""}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => cart.setQty(i.key || i.product_id, i.quantity - 1)} className="w-8 h-8 border border-hair flex items-center justify-center hover:border-ink" data-testid={`cart-dec-${i.product_id}`} aria-label="Riduci quantità">
                    <Minus size={12} />
                  </button>
                  <span className="text-sm w-6 text-center">{i.quantity}</span>
                  <button onClick={() => cart.setQty(i.key || i.product_id, i.quantity + 1)} className="w-8 h-8 border border-hair flex items-center justify-center hover:border-ink" data-testid={`cart-inc-${i.product_id}`} aria-label="Aumenta quantità">
                    <Plus size={12} />
                  </button>
                  <button onClick={() => cart.remove(i.key || i.product_id)} className="ml-auto text-taupe hover:text-red-700 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid={`cart-remove-${i.product_id}`} aria-label="Rimuovi">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-hair p-6 space-y-3">
          <div className="flex justify-between text-sm"><span className="text-taupe">Subtotale</span><span data-testid="cart-subtotal">{eur(cart.total)}</span></div>
          <div className="flex justify-between text-sm">
            <span className="text-taupe">Spedizione</span>
            <span data-testid="cart-shipping">{cart.items.length === 0 ? "—" : shippingNow === 0 ? "Gratuita" : eur(shippingNow)}</span>
          </div>
          <div className="flex justify-between items-baseline border-t border-hair pt-3">
            <span className="eyebrow">Totale</span>
            <span className="font-serif text-3xl" data-testid="cart-total">{eur(cart.items.length === 0 ? 0 : cart.total + shippingNow)}</span>
          </div>
          <p className="text-[11px] text-taupe">Imposte incluse ove applicabili. Le tempistiche sono indicative e decorrono dalla conferma dell'ordine.</p>
          <button
            disabled={cart.items.length === 0}
            onClick={() => { onOpenChange(false); navigate("/checkout"); }}
            className="btn-ink w-full min-h-[48px] disabled:opacity-40"
            data-testid="cart-checkout"
          >
            Vai al checkout
          </button>
          {onInquiry && (
            <button
              disabled={cart.items.length === 0}
              onClick={() => { onOpenChange(false); onInquiry(); }}
              className="w-full min-h-[48px] text-xs tracking-[0.14em] uppercase border border-ink hover:bg-ink hover:text-bone transition-colors disabled:opacity-40"
              data-testid="cart-inquiry"
            >
              Richiedi prezzo esclusivo
            </button>
          )}
          <button onClick={() => onOpenChange(false)} className="w-full text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink min-h-[44px]" data-testid="cart-continue">
            Continua gli acquisti
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
